import './DocumentModal.scss';
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import Button from '@atoms/Button';
import { MdClose } from 'react-icons/md';
import Modal from '@atoms/Modal';
import { Controller, SubmitErrorHandler, useForm } from 'react-hook-form';
import { IDocumentData } from '@models/document/IDocumentData';
import Preloader from '@atoms/Preloader';
import { sendModalMsg } from '@molecules/ModalMessages';
import DocumentTabBuilder from '@organisms/document/DocumentTabBuilder/DocumentTabBuilder';
import { IField, IFieldElem } from '@models/IFormData';
import { convertListToMap, getValueString } from '@utils/documentUtils';
import { VariablesManager } from '@utils/managers/VariablesManager';
import { useTypedSelector } from '@hooks/useTypedSelector';
import PageWrapper from '@atoms/PageWrapper';
import { v4 as uuidv4 } from 'uuid';
import DocumentModalTitle from '@organisms/DocumentModal/DocumentModalTitle/DocumentModalTitle';
import { IDocumentScheme } from '@models/document/IDocumentScheme';
import { ValidatorManager } from '@utils/managers/ValidatorManager';
import { NotificationsManager } from '@utils/managers/NotificationsManager';
import { ModalSize } from '@/types/ModalSize';
import BackdropLoader from '@atoms/BackdropLoader';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@controls/HookFormProvider';
import { DocumentModalContext } from '@/context/Contexts';
import { docInvalidFormHandler } from '@utils/helpers';

export interface IDocumentModalProps {
    /** Схема документа */
    scheme?: IDocumentScheme;
    /** Данные документа */
    data?: IDocumentData;
    /** Загрузка */
    isLoading: boolean;
    /** Режим редактирования */
    isEdit: boolean;
    /** Режим создания нового документа */
    isNew: boolean;
    /** Сохранение документа */
    onSave: (data: IDocumentData) => Promise<void>;
    /** Закрытие окна */
    onCancel: () => void;
    /** Размер окна */
    modalSize?: ModalSize;
    /** Размер индикатора загрузки */
    preloaderSize?: 's' | 'm' | 'l' | 'xl';
    /** Текст кнопки Сохранить */
    saveButtonText?: string;
    /** Высота во весь экран */
    fullHeight?: boolean;
}

const DocumentModal = ({
    scheme,
    data,
    isLoading,
    isEdit,
    isNew,
    onSave,
    onCancel,
    modalSize = 'max',
    preloaderSize = 'xl',
    saveButtonText = 'Сохранить',
    fullHeight = true,
}: IDocumentModalProps) => {
    const [fieldsData, setFieldsData] = useState<Record<string, IFieldElem>>();
    const [documentTitle, setDocumentTitle] = useState<string>();
    const [idForm, setIdForm] = useState(() => {
        return uuidv4();
    });

    const auth = useTypedSelector((state) => state.auth);

    const methods = useForm<IDocumentData>({
        mode: 'onBlur', // "onChange"
    });

    const initFieldsData = async (list: IField[], creator: string) => {
        let fields = convertListToMap(list);
        fields['doc_Creator'] = {
            name: 'doc_Creator',
            value: creator,
            index: -1,
        };
        if (scheme?.form.view.variables && data) {
            let mng = new VariablesManager(scheme.form.view.variables, data.id, auth.user);
            let variables = await mng.GetData(fields, isEdit, isNew);
            console.log(variables);
            fields['variables'] = {
                name: 'variables',
                value: variables,
                index: -1,
            };
        }

        setFieldsData(fields);
    };

    const getDocumentTitle = () => {
        if (scheme?.isTemplate) {
            setDocumentTitle('Шаблон "' + scheme.templateName + '" (' + scheme.flowTitle + ')');
            return;
        }

        if (fieldsData && fieldsData['variables'] && fieldsData['variables'].value['caption']) {
            setDocumentTitle(fieldsData['variables'].value['caption']);
        } else {
            setDocumentTitle(
                getValueString('|Document|Имя_потока', fieldsData!) +
                    ' ' +
                    getValueString('|Document|Регистрационный_номер', fieldsData!),
            );
        }
    };

    useEffect(() => {
        if (data && scheme) {
            initFieldsData(data.fields, data.doc_Creator);
            setIdForm(uuidv4());
            methods.reset(data);
        }
    }, [data, scheme]);

    useEffect(() => {
        if (fieldsData && scheme) {
            getDocumentTitle();
        }
    }, [fieldsData, isEdit, scheme]);

    const submitData = async (data: IDocumentData) => {
        let errors: string[] = [];
        let notify: string[] = [];
        let validatorManager = new ValidatorManager(fieldsData!, methods);
        let notificationsManager = new NotificationsManager(fieldsData!, methods);
        await validatorManager.validateAllAsync(scheme?.form.view.validators?.validator, errors);
        if (errors.length === 0) {
            await notificationsManager.notificationsAllAsync(scheme?.form.view.notifications?.messages, notify);

            if (notify.length > 0) {
                sendModalMsg({
                    message: notify,
                    closeBtnClick: async () => {
                        await onSave(data);
                    },
                    title: scheme?.form.view.notifications?.title,
                    type: 'warning',
                });
            } else {
                await onSave(data);
            }
        } else {
            sendModalMsg({
                message: errors,
                type: 'error',
            });
        }
    };

    const onSubmit = async (data: IDocumentData) => {
        await submitData(data);
    };

    const modalHeader = (
        <div className={clsx('document-modal__header')}>
            <div className={clsx('document-modal__header-title')}>
                {fieldsData ? <DocumentModalTitle titleText={documentTitle} fieldsData={fieldsData} /> : null}
            </div>
            <div className={clsx('document-modal__header-close')}>
                <Button
                    buttonType="text"
                    textColor="neutral"
                    size="xs"
                    aria-label="Закрыть окно"
                    onClick={() => {
                        onCancel();
                    }}
                    startAdornment={<MdClose size="24" />}
                />
            </div>
        </div>
    );

    const modalFooter = (
        <div className={clsx('document-modal__footer')}>
            {(isNew || isEdit) && (
                <Button
                    buttonType="primary"
                    className="doc-save-btn"
                    data-testid="document-save-button"
                    size="s"
                    type="submit"
                    form={idForm}
                    aria-label="Сохранить"
                    disabled={methods.formState.isSubmitting}
                >
                    {saveButtonText}
                </Button>
            )}
        </div>
    );

    const onInvalid: SubmitErrorHandler<any> = (errors) => {
        fieldsData && docInvalidFormHandler(errors, fieldsData);
    };

    return (
        <Modal
            className={clsx('document-modal', fullHeight && 'document-modal--full-height')}
            size={modalSize}
            header={modalHeader}
            footer={modalFooter}
        >
            {isLoading || !fieldsData || !data || !scheme ? (
                <div className="document-modal__busy-loader">
                    <Preloader size={preloaderSize} position="static" />
                </div>
            ) : (
                <FormProviderControl
                    formMethods={methods}
                    className="document-modal__form"
                    id={idForm}
                    onSubmit={onSubmit}
                    onInvalid={onInvalid}
                >
                    <HookFormProvider controller={Controller}>
                        <PageWrapper className="document-modal__wrapper">
                            <DocumentModalContext value={{ data: data, scheme: scheme }}>
                                <DocumentTabBuilder
                                    docId={data.id}
                                    baseDocId={data.baseDocKey!}
                                    scheme={scheme}
                                    methods={methods}
                                    setError={(errors?: string[]) => {
                                        if (errors) {
                                            sendModalMsg({
                                                message: errors,
                                                type: 'error',
                                            });
                                        }
                                    }}
                                    fields={fieldsData}
                                    isNew={isNew}
                                    isEdit={isEdit}
                                    turnOffInvokeChangesOnNew={false}
                                    useLinks={false}
                                    hideRoutes={true}
                                    hideStatusBar={true}
                                    hideRoutesMap={true}
                                    hideEisLog={true}
                                />
                            </DocumentModalContext>
                        </PageWrapper>
                        {methods.formState.isSubmitting && <BackdropLoader />}
                    </HookFormProvider>
                </FormProviderControl>
            )}
        </Modal>
    );
};

export default DocumentModal;
